import { Box, Heading, Stack, Text } from "@vinomofo/components";
import { LinkTo, StorefrontImage } from "../../types";

import { Button } from "../button";
import { ContentfulLink } from "../../types/contentful-types";
import { GatsbyImage } from "gatsby-plugin-image";
import { Link } from "../link";
import styled from "styled-components";

const DEFAULT_BACKGROUND_COLOR = "darkMofo";
const DEFAULT_TITLE_COLOR = "white";
const DEFAULT_SUBTITLE_COLOR = "#ff6d21";

const gridDirection = {
  left: "ltr",
  right: "rtl",
};

const objectPositionMap = {
  left: "center right",
  right: "center left",
};

const Container = styled(Box)<{ direction: string }>`
  direction: ${({ direction }) => direction};

  p,
  h3 {
    direction: ltr;
  }
`;

const imagePositions = ["left", "right"] as const;
export type ImagePosition = typeof imagePositions[number];

const textAlignments = ["left", "center", "right"] as const;
export type TextAlignment = typeof textAlignments[number];

export interface SplitBannerProps {
  image: StorefrontImage;
  imagePosition: ImagePosition;
  textAlign: TextAlignment;
  backgroundColor: string;
  headerImage: StorefrontImage;
  title?: string;
  subtitle?: string;
  bodyText?: string;
  titleColor?: string;
  subtitleColor?: string;
  bodyTextColor?: string;
  primaryCta: {
    buttonColour: string;
    buttonBorderColor: string;
    buttonTextColor: string;
    label: string;
    linkTo: LinkTo;
  };
}

export const SplitBanner = ({
  image,
  headerImage,
  imagePosition,
  textAlign = "left",
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  titleColor = DEFAULT_TITLE_COLOR,
  subtitleColor = DEFAULT_SUBTITLE_COLOR,
  bodyTextColor = DEFAULT_SUBTITLE_COLOR,
  title,
  subtitle,
  bodyText,
  primaryCta,
}: SplitBannerProps): JSX.Element => {
  const ctaLink = primaryCta?.linkTo?.slug || primaryCta?.linkTo?.url;
  const ctaTarget = primaryCta?.linkTo?.target === "New Tab" ? "_new" : "_self";
  return (
    <Container
      display="grid"
      gridTemplateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
      gridGap="0"
      gridAutoRows={["auto", "1fr"]}
      width="100%"
      direction={gridDirection[imagePosition]}
      backgroundColor={backgroundColor}
      borderRadius="4px"
      overflow="hidden"
    >
      <GatsbyImage
        alt=""
        image={image}
        objectFit="cover"
        objectPosition={objectPositionMap[imagePosition]}
      />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding={["20px", "30px", "40px"]}
        textAlign={["center", textAlign]}
      >
        <Stack space={3}>
          {subtitle && (
            <Text fontWeight={500} color={subtitleColor} fontFamily="heading">
              {subtitle}
            </Text>
          )}
          {headerImage && (
            <GatsbyImage
              alt={`Image of ${title}`}
              image={headerImage}
              objectFit="contain"
              objectPosition="center center"
            />
          )}
          {headerImage ? (
            <Text color={titleColor} fontWeight={500}>
              {title}
            </Text>
          ) : (
            <Heading as="h3" variant="lg" color={titleColor}>
              {title}
            </Heading>
          )}
          {bodyText && (
            <Text color={bodyTextColor} fontWeight={500}>
              {bodyText}
            </Text>
          )}
          <Box>
            {ctaLink && (
              <Button
                to={ctaLink}
                target={ctaTarget}
                as={Box}
                variant="outline"
                border="2px solid"
                borderColor={
                  primaryCta.buttonBorderColor ||
                  primaryCta.buttonColour ||
                  "white"
                }
                backgroundColor={
                  primaryCta.buttonColour ||
                  primaryCta.buttonBorderColor ||
                  "white"
                }
                color={primaryCta.buttonTextColor || "black"}
                outline
                isCTA
              >
                {primaryCta.label}
              </Button>
            )}
          </Box>
        </Stack>
      </Box>
    </Container>
  );
};
